import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import { SEO } from "@/components/SEO";
import { Logo } from "@/components/ui/logo";

const sections = [
  {
    title: "1. Definitions",
    content: [
      `"Controller" means the Customer who determines the purposes and means of the processing of Personal Data by using Reqqy's services.`,
      `"Processor" means Reqqy (operated by its parent entity), which processes Personal Data on behalf of the Controller.`,
      `"Personal Data" means any information relating to an identified or identifiable natural person, as defined in the GDPR.`,
      `"Processing" means any operation performed on Personal Data, including collection, storage, retrieval, use, and deletion.`,
      `"Sub-Processor" means a third party engaged by the Processor to process Personal Data on behalf of the Controller.`,
      `"Data Subject" means the identified or identifiable natural person to whom Personal Data relates.`,
    ],
  },
  {
    title: "2. Scope and Purpose of Processing",
    content: [
      "This DPA applies to all Personal Data processed by Reqqy on behalf of the Customer in connection with the provision of the Reqqy requirements review service.",
      "Reqqy processes Personal Data solely for the purpose of providing the services described in the applicable service agreement, including: user account management, requirements review execution, project and organisation management, billing and payment processing, and customer support.",
      "The categories of Personal Data processed include: name, email address, account credentials (hashed), organisation details, requirements text submitted for review, and usage analytics.",
      "The categories of Data Subjects include: Customer employees, contractors, and other authorised users who access the Reqqy platform.",
    ],
  },
  {
    title: "3. Obligations of the Processor",
    content: [
      "Reqqy shall process Personal Data only on documented instructions from the Controller, including with regard to transfers to third countries, unless required by applicable law.",
      "Reqqy shall ensure that persons authorised to process Personal Data have committed themselves to confidentiality or are under an appropriate statutory obligation of confidentiality.",
      "Reqqy shall implement appropriate technical and organisational measures to ensure a level of security appropriate to the risk, including encryption of data at rest (AES-256) and in transit (TLS 1.3), access controls, and regular security assessments.",
      "Reqqy shall not engage another processor (Sub-Processor) without prior written authorisation from the Controller. Where Sub-Processors are engaged, Reqqy shall impose equivalent data protection obligations.",
      "Reqqy shall assist the Controller in fulfilling its obligations to respond to Data Subject requests under the GDPR, including requests for access, rectification, erasure, and data portability.",
      "Reqqy shall assist the Controller in ensuring compliance with security obligations, breach notification, data protection impact assessments, and prior consultations with supervisory authorities.",
    ],
  },
  {
    title: "4. Sub-Processors",
    content: [
      "The Controller provides general authorisation for Reqqy to engage Sub-Processors, subject to the conditions in this section.",
      "Reqqy maintains a list of current Sub-Processors, which is available upon request. Current Sub-Processors include: cloud infrastructure providers (for hosting and data storage), payment processors (for billing), email service providers (for transactional communications), and AI model providers (for requirements analysis processing).",
      "Reqqy shall notify the Controller of any intended changes to Sub-Processors, giving the Controller the opportunity to object within 30 days. If the Controller objects on reasonable grounds, the parties shall discuss in good faith a resolution.",
      "Where Reqqy engages a Sub-Processor, a written contract shall be concluded imposing the same data protection obligations as set out in this DPA.",
    ],
  },
  {
    title: "5. International Transfers",
    content: [
      "Personal Data is stored in the European Union (Frankfurt, Germany) by default.",
      "Where transfers of Personal Data to countries outside the EU/EEA are necessary (e.g., for Sub-Processor operations), Reqqy shall ensure appropriate safeguards are in place, such as Standard Contractual Clauses (SCCs) adopted by the European Commission, or other approved transfer mechanisms.",
      "Enterprise customers may select alternative data residency regions (US-East, US-West, Asia-Pacific) through their service agreement.",
    ],
  },
  {
    title: "6. Data Subject Rights",
    content: [
      "Reqqy shall, taking into account the nature of the processing, assist the Controller by appropriate technical and organisational measures for the fulfilment of the Controller's obligation to respond to requests for exercising the Data Subject's rights under the GDPR.",
      "These rights include: right of access (Article 15), right to rectification (Article 16), right to erasure (Article 17), right to restriction of processing (Article 18), right to data portability (Article 20), and right to object (Article 21).",
      "Users can exercise their data rights directly through their Reqqy account settings, including downloading their data and deleting their account.",
    ],
  },
  {
    title: "7. Data Breach Notification",
    content: [
      "In the event of a Personal Data breach, Reqqy shall notify the Controller without undue delay, and in any event within 72 hours of becoming aware of the breach.",
      "The notification shall include: a description of the nature of the breach, the categories and approximate number of Data Subjects and records concerned, the likely consequences of the breach, and the measures taken or proposed to address the breach.",
      "Reqqy shall cooperate with the Controller and take reasonable commercial steps to assist in the investigation, mitigation, and remediation of each such breach.",
    ],
  },
  {
    title: "8. Audit Rights",
    content: [
      "The Controller has the right to conduct audits, including inspections, to verify Reqqy's compliance with this DPA. Audits shall be conducted with reasonable notice and during normal business hours.",
      "Reqqy shall make available to the Controller all information necessary to demonstrate compliance with the obligations laid down in this DPA and the GDPR.",
      "Where an audit is conducted by a third-party auditor, the auditor must be bound by confidentiality obligations. Reqqy may object to an auditor if the auditor is a direct competitor.",
    ],
  },
  {
    title: "9. Data Retention and Deletion",
    content: [
      "Upon termination of the service agreement, Reqqy shall, at the choice of the Controller, delete or return all Personal Data to the Controller and delete existing copies, unless applicable law requires retention.",
      "The Controller may request data export at any time through the platform's settings or by contacting support. Data exports are provided in machine-readable formats (JSON, CSV).",
      "Following account deletion, all Personal Data is permanently removed from primary systems immediately and from backup systems within 30 days.",
    ],
  },
  {
    title: "10. Term and Governing Law",
    content: [
      "This DPA shall remain in effect for the duration of the service agreement between the parties, and for as long as Reqqy processes Personal Data on behalf of the Controller.",
      "This DPA shall be governed by and construed in accordance with the laws of the jurisdiction specified in the service agreement. In the absence of such specification, the laws of Ireland shall apply.",
      "Any disputes arising from this DPA shall be resolved in accordance with the dispute resolution mechanisms set out in the service agreement.",
    ],
  },
];

export default function DPA() {
  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="Data Processing Agreement"
        description="Reqqy's Data Processing Agreement (DPA) outlines how we process personal data in compliance with GDPR and applicable data protection laws."
        canonical="/dpa"
      />

      {/* Header */}
      <header className="border-b border-border">
        <div className="container flex items-center justify-between h-16 px-4 md:px-6">
          <Link to="/" className="flex items-center gap-2">
            <Logo variant="full" className="h-8 w-auto" />
          </Link>
          <Button asChild variant="ghost" size="sm">
            <Link to="/home">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Home
            </Link>
          </Button>
        </div>
      </header>

      <main className="container px-4 md:px-6 py-12 md:py-16 max-w-4xl">
        <h1 className="text-3xl md:text-4xl font-display font-bold mb-4">Data Processing Agreement</h1>
        <p className="text-muted-foreground mb-2">Last updated: February 1, 2026</p>
        <p className="text-lg text-muted-foreground mb-12">
          This Data Processing Agreement ("DPA") forms part of the service agreement between the Customer ("Controller")
          and Reqqy ("Processor") and sets out the terms under which Reqqy processes Personal Data on behalf of the
          Customer.
        </p>

        <div className="space-y-8">
          {sections.map(({ title, content }) => (
            <section key={title} className="prose prose-neutral dark:prose-invert max-w-none">
              <h2 className="text-xl font-semibold mb-3">{title}</h2>
              <div className="space-y-3">
                {content.map((paragraph, idx) => (
                  <p key={idx} className="text-muted-foreground text-sm leading-relaxed">
                    {paragraph}
                  </p>
                ))}
              </div>
            </section>
          ))}
        </div>

        {/* Contact */}
        <div className="mt-12 p-8 rounded-xl border border-border bg-muted/50 text-center">
          <h2 className="text-xl font-semibold mb-2">Need a signed DPA?</h2>
          <p className="text-muted-foreground mb-4">
            Enterprise and Business plan customers can request a countersigned copy of this DPA. Contact our legal team
            to get started.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button asChild>
              <a href="mailto:legal@reqqy.app">Contact Legal Team</a>
            </Button>
            <Button asChild variant="outline">
              <Link to="/security">View Security Page</Link>
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
}
